import Division from '../../components/Division';
import type { MenuState } from '@features/main/interfaces/main.interfaces';
import { ClienteSuite, DivisionCliente } from '@interfaces/firestore-documents.interfaces';
import { useEffect, useRef, useState } from 'react';
import useDivisionsContainerStyles from './DivisionsContainer.styles';
import {
	DivisionsFiltersForm,
	FiltersNoResults,
	FiltersStates,
	InputSelectOptions,
	TitleHeaderContainer,
} from '@features/filters';
import { SEARCH_CONTAINER_HEIGHT } from '@features/customers';
import { useForm } from 'react-hook-form';
import { Search } from '@mui/icons-material';
import {
	handleFiltersClearClick,
	handleFiltersKeyPress,
	searchDivisions,
} from './DivisionsContainer.handlers';
import clsx from 'clsx';
import { InfiniteScroll } from '@features/common';

interface Props {
	data: ClienteSuite;
	menuState: MenuState;
	setDivisionNumber: React.Dispatch<React.SetStateAction<string>>;
}

const DivisionsContainer: React.FC<Props> = ({
	data: { divisiones, identificacion, nombre, rangos_cliente },
	menuState,
	setDivisionNumber,
}) => {
	// Refs
	const divisionsContainer = useRef<HTMLDivElement | null>(null);

	// States
	const [divisionsData, setDivisionsData] = useState<DivisionCliente[]>([]);
	const [searchedProcess, setSearchedProcess] = useState<string>('');
	const [scrollPage, setScrollPage] = useState<number>(0);
	const [divisionKeyPress, setDivisionKeyPress] = useState(false);
	const [processKeyPress, setProcessKeyPress] = useState(false);

	// Hooks
	const classes = useDivisionsContainerStyles();
	const filtersForm = useForm<DivisionsFiltersForm>({
		defaultValues: {
			process: '',
			division: '',
			state: FiltersStates.All,
		},
	});

	// Variables
	const rangosCliente = rangos_cliente !== undefined ? rangos_cliente : [];
	const headerContainer = document.getElementById('customer-information-divisions-header');
	const division = filtersForm.watch('division');
	const process = filtersForm.watch('process');
	const state = filtersForm.watch('state');

	const filtersValues: DivisionsFiltersForm = { division, process, state };
	const resultsHeight = `calc(100vh - ${SEARCH_CONTAINER_HEIGHT} - ${
		headerContainer?.offsetHeight ?? 0
	}px)`;

	// Effects
	useEffect(() => {
		searchDivisions(divisiones, filtersValues, setDivisionsData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [divisiones, state]);

	const getCounter = () => {
		return (
			Object.keys(filtersForm.formState.dirtyFields).length +
			(filtersValues.division && divisionKeyPress ? 1 : 0) +
			(filtersValues.process && processKeyPress ? 1 : 0)
		);
	};

	return (
		<div className={classes.root} id="customer-information-divisions">
			<TitleHeaderContainer
				rangosCliente={rangosCliente}
				className={classes.title}
				counter={getCounter() ?? 0}
				id="customer-information-divisions-header"
				inputs={[
					{
						control: filtersForm.control,
						defaultValue: '',
						name: 'process',
						onClear: handleFiltersClearClick(
							'process',
							divisiones,
							filtersValues,
							setDivisionsData,
							setSearchedProcess,
							setDivisionKeyPress,
							setProcessKeyPress
						),
						onKeyPress: handleFiltersKeyPress(
							divisiones,
							filtersValues,
							setDivisionsData,
							setSearchedProcess,
							setDivisionKeyPress,
							setProcessKeyPress
						),
						onChange: () => {
							return async (e) => {
								if (e) {
									filtersForm.setValue('process', e.target.value);
									if (e.target.value === '') {
										searchDivisions(
											divisiones,
											filtersForm.getValues(),
											setDivisionsData
										);
										await setSearchedProcess(filtersForm.getValues().process);
										if (
											document.getElementsByClassName('searched').length > 0
										) {
											document
												.getElementsByClassName('searched')[0]
												.scrollIntoView();
										}
										setProcessKeyPress(false);
									}
								} else {
									filtersForm.setValue('process', '');
								}
							};
						},
						placeholder: 'Escribe el proceso',
						startAdorment: <Search fontSize="small" />,
					},
					{
						control: filtersForm.control,
						defaultValue: '',
						name: 'division',
						onClear: handleFiltersClearClick(
							'division',
							divisiones,
							filtersValues,
							setDivisionsData,
							setSearchedProcess,
							setDivisionKeyPress,
							setProcessKeyPress
						),
						onKeyPress: handleFiltersKeyPress(
							divisiones,
							filtersValues,
							setDivisionsData,
							setSearchedProcess,
							setDivisionKeyPress,
							setProcessKeyPress
						),
						onChange: () => {
							return async (e) => {
								if (e) {
									filtersForm.setValue('division', e.target.value);
									if (e.target.value === '') {
										searchDivisions(
											divisiones,
											filtersForm.getValues(),
											setDivisionsData
										);
										await setSearchedProcess(filtersForm.getValues().division);
										if (
											document.getElementsByClassName('searched').length > 0
										) {
											document
												.getElementsByClassName('searched')[0]
												.scrollIntoView();
										}
										setDivisionKeyPress(false);
									}
								} else {
									filtersForm.setValue('division', '');
								}
							};
						},
						placeholder: 'Escribe la división',
						startAdorment: <Search fontSize="small" />,
					},
					{
						control: filtersForm.control,
						defaultValue: FiltersStates.All,
						name: 'state',
						options: Object.values(FiltersStates).map((value) => ({
							value,
							label: value.toString(),
						})),
						width: 211,
					} as InputSelectOptions<DivisionsFiltersForm, FiltersStates>,
				]}
				title={`Divisiones: ${nombre}`}
			/>
			<div
				className={clsx(classes.divisionsContainer, {
					[classes.divisionsContainerEmpty]: divisionsData.length === 0,
				})}
				id="customer-information-divisions-scroll"
				ref={divisionsContainer}
				style={{ height: resultsHeight, maxHeight: resultsHeight }}
			>
				{divisionsData.length > 0 ? (
					<InfiniteScroll
						className={classes.scrollContainer}
						dataLength={divisionsData.length}
						loadMore={setScrollPage}
						parentElement={divisionsContainer}
					>
						{divisionsData.slice(0, scrollPage * 6).map((division, i) => (
							<Division
								data={division}
								key={`division_${identificacion}_${division.codigo_div}_${i}`}
								menuState={menuState}
								searchedProcess={searchedProcess.toLowerCase()}
								setDivisionNumber={setDivisionNumber}
								tabIndex={i + 1}
							/>
						))}
					</InfiniteScroll>
				) : (
					<FiltersNoResults height={resultsHeight} />
				)}
			</div>
		</div>
	);
};

export default DivisionsContainer;
